import { supabase } from "../lib/supabaseClient";
import { buildTeamSearchPattern } from "../utils/searchFold";
import type { ReadinessRow } from "../lib/season/prepTarget";

export type SeasonStatus = "upcoming" | "active" | "archived";

export interface SeasonRow {
  code: string;
  label_fr: string;
  label_ar: string;
  start_date: string;
  end_date: string;
  status: SeasonStatus;
  is_default: boolean;
}

export type TransitionStatus =
  | "draft"
  | "planning"
  | "promotions_locked"
  | "groups_created"
  | "rosters_carried"
  | "activated"
  | "rolled_back";

export type TeamMovement =
  | "promotion"
  | "relegation"
  | "retained"
  | "withdrawn"
  | "admitted"
  | "undecided";

export interface SeasonTransitionRow {
  id: string;
  from_season: string;
  to_season: string;
  status: TransitionStatus;
  initiated_by: string | null;
  initiated_at: string;
  activated_at: string | null;
  steps: Record<string, { at: string; by: string | null; rows_affected: number; notes: string | null }>;
  overrides: { calendar_skipped?: Record<string, true> } | null;
  notes: string | null;
}

export interface TeamMoveRow {
  transition_id: string;
  team_id: string;
  from_league_id: string | null;
  from_group_id: string | null;
  to_league_id: string | null;
  to_group_id: string | null;
  movement_type: TeamMovement;
  rank_basis: Record<string, unknown> | null;
  decided_by: string | null;
  decided_at: string | null;
  notes: string | null;
}

export interface PromotionRule {
  id: string;
  league_id: string;
  league_group_id: string | null;
  season: string | null;
  promotions_count: number;
  relegations_count: number;
  promotes_to_league_id: string | null;
  relegates_to_league_id: string | null;
  notes: string | null;
  is_active: boolean;
}

// ── Promotion rules CRUD ────────────────────────────────────────────────────

export async function listPromotionRules(): Promise<PromotionRule[]> {
  const { data, error } = await supabase
    .from("league_promotion_rules")
    .select("*")
    .eq("is_active", true)
    .order("league_id");
  if (error) throw error;
  return (data ?? []) as PromotionRule[];
}

export async function upsertPromotionRule(
  input: Omit<PromotionRule, "id" | "is_active"> & { id?: string }
): Promise<PromotionRule> {
  const payload = { ...input, is_active: true };
  const { data, error } = await supabase
    .from("league_promotion_rules")
    .upsert(payload, { onConflict: "id" })
    .select()
    .single();
  if (error) throw error;
  return data as PromotionRule;
}

/** Soft-delete a promotion rule (group override or default) — sets is_active=false. */
export async function deletePromotionRule(id: string): Promise<void> {
  const { error } = await supabase
    .from("league_promotion_rules")
    .update({ is_active: false })
    .eq("id", id);
  if (error) throw error;
}

// ── Promotion impact preview ────────────────────────────────────────────────

export interface PromotionImpactTeam {
  team_id: string;
  name: string;
  rank: number;
  /** Relegated teams only: used for the wilaya → regional-league auto-resolution. */
  wilaya?: string | null;
  to_league_id: string | null;
  to_league_code: string | null;
  /** True when no rule target exists AND the wilaya fallback failed — needs attention. */
  unresolved?: boolean;
}

export interface PromotionImpactGroup {
  group_id: string;
  group_name: string;
  group_code: string | null;
  league_id: string;
  league_code: string;
  teams_in_group: number;
  rule_scope: "none" | "league" | "group" | "season" | "group+season";
  promotions_count: number;
  relegations_count: number;
  promotes_to_league_id: string | null;
  promotes_to_league_code: string | null;
  promoted: PromotionImpactTeam[];
  relegated: PromotionImpactTeam[];
}

export interface PromotionImpactCup {
  group_id: string;
  group_name: string;
  league_code: string;
  promotes_to_league_id: string | null;
  promotes_to_league_code: string | null;
  leader_team_id: string | null;
  leader_name: string | null;
}

export interface PromotionImpactPreview {
  from_season: string;
  groups: PromotionImpactGroup[];
  cups: PromotionImpactCup[];
}

/**
 * Dry-run of the promotion/relegation rules against a season's current
 * standings: who goes up, who goes down, and to where (read-only RPC).
 */
export async function previewPromotionImpact(
  fromSeason: string,
  leagueId?: string | null
): Promise<PromotionImpactPreview> {
  const { data, error } = await supabase.rpc("preview_promotion_impact", {
    p_from_season: fromSeason,
    p_league_id: leagueId ?? null,
  });
  if (error) throw error;
  const raw = (data ?? {}) as Partial<PromotionImpactPreview>;
  return {
    from_season: raw.from_season ?? fromSeason,
    groups: raw.groups ?? [],
    cups: raw.cups ?? [],
  };
}

/**
 * Clone the from-season cup/play-off groups into the transition's to-season
 * (qualifies_from remapped, promotes_winner_to_league_id preserved, no teams).
 * Returns the number of groups created; 0 if all already exist. Admin-gated.
 */
export async function carryForwardCupGroups(transitionId: string): Promise<number> {
  const { data, error } = await supabase.rpc("carry_forward_cup_groups", {
    p_transition_id: transitionId,
  });
  if (error) throw error;
  return typeof data === "number" ? data : 0;
}

// ── Seasons ─────────────────────────────────────────────────────────────────

export async function listSeasons(): Promise<SeasonRow[]> {
  const { data, error } = await supabase
    .from("seasons")
    .select("*")
    .order("start_date");
  if (error) throw error;
  return (data ?? []) as SeasonRow[];
}

export async function createSeason(input: Omit<SeasonRow, "is_default">): Promise<SeasonRow> {
  const { data, error } = await supabase
    .from("seasons")
    .insert({ ...input, is_default: false })
    .select()
    .single();
  if (error) throw error;
  return data as SeasonRow;
}

/**
 * Create an UPCOMING season as a real `seasons` row (idempotent; admin-gated RPC).
 * Labels default to the code, dates to Sep 1 → Jun 30. The seasons→app_settings
 * trigger then makes the season appear in the Header selector automatically.
 */
export async function createSeasonRow(input: {
  code: string;
  labelFr?: string;
  labelAr?: string;
  startDate?: string;
  endDate?: string;
}): Promise<{ code: string; created: boolean; existed?: boolean }> {
  const { data, error } = await supabase.rpc("create_season", {
    p_code: input.code,
    p_label_fr: input.labelFr ?? null,
    p_label_ar: input.labelAr ?? null,
    p_start_date: input.startDate ?? null,
    p_end_date: input.endDate ?? null,
  });
  if (error) throw error;
  return (data ?? { code: input.code, created: false }) as {
    code: string;
    created: boolean;
    existed?: boolean;
  };
}

/**
 * Make a season the single active + default one (archives the current active),
 * standalone — no season transition required. Admin-gated RPC.
 */
export async function activateSeasonStandalone(
  code: string
): Promise<{ activated: string; previous_active: string | null }> {
  const { data, error } = await supabase.rpc("activate_season", { p_season_code: code });
  if (error) throw error;
  return (data ?? { activated: code, previous_active: null }) as {
    activated: string;
    previous_active: string | null;
  };
}

// ── Simulator ───────────────────────────────────────────────────────────────

export async function simulateTransition(
  fromSeason: string,
  toSeason: string
): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.rpc("simulate_season_transition", {
    p_from_season: fromSeason,
    p_to_season: toSeason,
  });
  if (error) throw error;
  return data as Record<string, unknown>;
}

// ── Transitions ─────────────────────────────────────────────────────────────

export async function listTransitions(): Promise<SeasonTransitionRow[]> {
  const { data, error } = await supabase
    .from("season_transitions")
    .select("*")
    .order("initiated_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as SeasonTransitionRow[];
}

export async function createTransition(
  fromSeason: string,
  toSeason: string
): Promise<SeasonTransitionRow> {
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("season_transitions")
    .insert({
      from_season: fromSeason,
      to_season: toSeason,
      initiated_by: userData.user?.id ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  return data as SeasonTransitionRow;
}

export async function runStep(
  step:
    | "snapshot_final_standings"
    | "lock_old_season"
    | "prepare_team_moves"
    | "create_new_season_groups"
    | "carry_over_rosters"
    | "carry_over_staff_rosters"
    | "carry_over_pending_suspensions"
    | "carry_over_configurations"
    | "activate_season",
  transitionId: string
): Promise<number | null> {
  const { data, error } = await supabase.rpc(step, { p_transition_id: transitionId });
  if (error) throw error;
  return (typeof data === "number" ? data : null) as number | null;
}

export async function resetTransition(transitionId: string): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.rpc("reset_season_transition", {
    p_transition_id: transitionId,
  });
  if (error) throw error;
  return data as Record<string, unknown>;
}

import type { CarryoverSuspensionRow } from "../lib/seasonTransition/carryoverBuckets";
export type { CarryoverSuspensionRow };

/** Read-only preview of the suspension carry-over for a transition (unserved rows only). */
export async function previewCarryoverSuspensions(
  transitionId: string
): Promise<CarryoverSuspensionRow[]> {
  const { data, error } = await supabase.rpc("preview_carryover_suspensions", {
    p_transition_id: transitionId,
  });
  if (error) throw error;
  return (data ?? []) as CarryoverSuspensionRow[];
}

export interface CarryoverConfigsPreview {
  stadiums_pending: number;
  rules_pending: number;
}

/** Read-only preview of the configurations carry (stadiums + barème) for a transition. */
export async function previewCarryoverConfigurations(
  transitionId: string
): Promise<CarryoverConfigsPreview> {
  const { data, error } = await supabase.rpc("preview_carryover_configurations", {
    p_transition_id: transitionId,
  });
  if (error) throw error;
  const row = Array.isArray(data) ? data[0] : data;
  return {
    stadiums_pending: Number(row?.stadiums_pending) || 0,
    rules_pending: Number(row?.rules_pending) || 0,
  };
}

export type { ReadinessRow };

/** Per-league season-preparation readiness (single source of truth for the board + activation email). */
export async function getLeagueSeasonReadiness(
  season: string,
  leagueId: string | null
): Promise<ReadinessRow[]> {
  const { data, error } = await supabase.rpc("get_league_season_readiness", {
    p_season: season,
    ...(leagueId ? { p_league_id: leagueId } : {}),
  });
  if (error) throw error;
  return ((data ?? []) as any[]).map((r) => ({
    leagueId: r.league_id,
    leagueName: r.league_name,
    leagueNameAr: r.league_name_ar,
    groupsTotal: r.groups_total ?? 0,
    groupsWithoutTeams: r.groups_without_teams ?? 0,
    capacityShort: r.capacity_short ?? 0,
    calendarsReady: r.calendars_ready ?? 0,
    calendarsSkipped: r.calendars_skipped ?? 0,
    calendarsMissing: r.calendars_missing ?? 0,
    teamsPlaced: r.teams_placed ?? 0,
    capacityTarget: r.capacity_target ?? 0,
    stadiumsMissing: r.stadiums_missing ?? 0,
    transferWindowOk: !!r.transfer_window_ok,
    transferWindowState: r.transfer_window_state ?? "missing",
    calendarConfigExists: !!r.calendar_config_exists,
    accreditationsStale: r.accreditations_stale ?? 0,
    incomingUnplaced: r.incoming_unplaced ?? 0,
    socialConfigsStale: r.social_configs_stale ?? 0,
    numberingReady: !!r.numbering_ready,
  }));
}

/** Super-admin: escape (waive) or re-include a single suspension before the carry step runs. */
export async function setSuspensionCarryoverEscape(
  transitionId: string,
  sanctionId: string,
  escaped: boolean
): Promise<void> {
  const { error } = await supabase.rpc("set_suspension_carryover_escape", {
    p_transition_id: transitionId,
    p_sanction_id: sanctionId,
    p_escaped: escaped,
  });
  if (error) throw error;
}

// ── Team moves ──────────────────────────────────────────────────────────────

export async function listTeamMoves(transitionId: string): Promise<TeamMoveRow[]> {
  const { data, error } = await supabase
    .from("season_transition_team_moves")
    .select("*")
    .eq("transition_id", transitionId);
  if (error) throw error;
  return (data ?? []) as TeamMoveRow[];
}

export async function updateTeamMove(
  transitionId: string,
  teamId: string,
  patch: Partial<Pick<TeamMoveRow, "movement_type" | "to_league_id" | "to_group_id" | "notes">>
): Promise<TeamMoveRow> {
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("season_transition_team_moves")
    .update({ ...patch, decided_by: userData.user?.id ?? null, decided_at: new Date().toISOString() })
    .eq("transition_id", transitionId)
    .eq("team_id", teamId)
    .select()
    .single();
  if (error) throw error;
  return data as TeamMoveRow;
}

// ── Calendar config + generator ─────────────────────────────────────────────

export interface ExcludedDate {
  date: string;       // YYYY-MM-DD
  reason?: string;
}

export interface CalendarConfig {
  id: string;
  league_id: string;
  league_group_id: string | null;
  legs: 1 | 2;
  match_day_of_week: number | null;
  days_between_rounds: number;
  /**
   * Distinct calendar dates one round may span, and therefore a shared ground's
   * per-round match capacity. DB default 2; 1 restores the strict
   * one-host-per-round rule. CHECK: 1 <= days_per_round < days_between_rounds.
   */
  days_per_round: number;
  default_start_date: string | null;
  default_end_date: string | null;
  excluded_dates: ExcludedDate[];
  ramadan_start_date: string | null;
  ramadan_end_date: string | null;
  respect_ramadan: boolean;
  notes: string | null;
  is_active: boolean;
}

export async function listCalendarConfigs(): Promise<CalendarConfig[]> {
  const { data, error } = await supabase
    .from("league_calendar_config")
    .select("*")
    .eq("is_active", true)
    .order("league_id");
  if (error) throw error;
  return (data ?? []) as CalendarConfig[];
}

export async function upsertCalendarConfig(
  input: Omit<CalendarConfig, "id" | "is_active"> & { id?: string }
): Promise<CalendarConfig> {
  const { data, error } = await supabase
    .from("league_calendar_config")
    .upsert({ ...input, is_active: true }, { onConflict: "id" })
    .select()
    .single();
  if (error) throw error;
  return data as CalendarConfig;
}

export interface CalendarGenerationResult {
  group_id: string;
  season?: string;
  teams?: number;
  rounds_per_leg?: number;
  legs?: number;
  total_matches?: number;
  start_date?: string | null;
  end_date?: string | null;
  match_day_of_week?: number | null;
  excluded_dates?: ExcludedDate[];
  ramadan_start_date?: string | null;
  ramadan_end_date?: string | null;
  respect_ramadan?: boolean;
  skipped_dates?: string[];
  /**
   * Number of shared-ground conflicts the generator acted on.
   *
   * TWO producers fill these three slots and their entry shapes differ:
   * - the CP-SAT container (`Optimiser`) spills a shared ground's second host in a
   *   round onto the next usable DATE — `calendar-optimizer/container/app/spillover.py`
   *   emits `{stadium_id, round, team_id, from_date, to_date}`.
   * - the `generate_round_robin_calendar` RPC (`Simuler`) permutes home/away instead —
   *   `resolve_stadium_conflicts` emits `{game_day, stadium_id, stadium_name, swapped_home_away}`.
   *
   * Fields are therefore optional per producer; read `round ?? game_day` for the
   * round number. `stadium_id` is null for a team with no ground on file.
   */
  stadium_swaps?: number;
  stadium_swap_log?: Array<{
    stadium_id: string | null;
    /** Container. 1-based round number — do NOT add 1 when rendering. */
    round?: number;
    /** Container. The spilled match's home team. */
    team_id?: string;
    /** Container. The round's primary date the match moved off. */
    from_date?: string;
    /** Container. The date the match moved to (the next usable day). */
    to_date?: string;
    /** RPC fast path only. 1-based round number. */
    game_day?: number;
    /** RPC fast path only — the container has no stadium names in hand. */
    stadium_name?: string | null;
    /** RPC fast path only. [previousHome, previousAway]. */
    swapped_home_away?: [string, string];
  }>;
  /** Conflicts the generator could not resolve — they need manual placement. */
  stadium_unresolved?: Array<{
    stadium_id: string | null;
    /** Container. 1-based round number — do NOT add 1 when rendering. */
    round?: number;
    /** Container. The home team left without a date. */
    team_id?: string;
    /** Container. The round date that had no free slot. */
    round_date?: string;
    /** RPC fast path only. 1-based round number. */
    game_day?: number;
    /** RPC fast path only. */
    stadium_name?: string | null;
    home_team_id?: string;
    away_team_id?: string;
    reason?: string;
  }>;
  pairings?: Array<{
    leg: number;
    game_day: number;
    match_date: string | null;
    home_team_id: string;
    away_team_id: string;
  }>;
  committed: boolean;
  error?: string;
}

export interface CalendarOverride {
  legs?: 1 | 2;
  start_date?: string;
  days_between_rounds?: number;
  match_day_of_week?: number;
  excluded_dates?: ExcludedDate[];
  ramadan_start_date?: string | null;
  ramadan_end_date?: string | null;
  respect_ramadan?: boolean;
  resolve_stadium_conflicts?: boolean;
}

export async function generateCalendar(
  groupId: string,
  dryRun: boolean,
  override: CalendarOverride = {}
): Promise<CalendarGenerationResult> {
  const { data, error } = await supabase.rpc("generate_round_robin_calendar", {
    p_group_id: groupId,
    p_dry_run: dryRun,
    p_override: override,
  });
  if (error) throw error;
  return data as CalendarGenerationResult;
}

// Commit an externally-produced set of pairings (e.g. from the OR-Tools
// optimizer preview) without regenerating via the circle method.
export async function commitCalendarFromPairings(
  groupId: string,
  pairings: NonNullable<CalendarGenerationResult["pairings"]>
): Promise<{ group_id: string; season?: string; total_matches?: number; committed: boolean; error?: string }> {
  const { data, error } = await supabase.rpc("commit_calendar_from_pairings", {
    p_group_id: groupId,
    p_pairings: pairings,
  });
  if (error) throw error;
  return data;
}

// ── Calendar candidates (generate several → compare → validate → production) ──

export interface CalendarCandidate {
  id: string;
  league_group_id: string;
  season: string;
  label: string;
  source: "optimizer" | "circle" | "manual" | "draw";
  constraints: Record<string, unknown>;
  weights: Record<string, unknown>;
  pairings: NonNullable<CalendarGenerationResult["pairings"]>;
  metrics: Record<string, unknown>;
  status: "candidate" | "validated" | "discarded";
  created_by: string | null;
  created_at: string;
  updated_by: string | null;
  updated_at: string;
}

export async function listCalendarCandidates(groupId: string): Promise<CalendarCandidate[]> {
  const { data, error } = await supabase
    .from("calendar_candidates")
    .select("*")
    .eq("league_group_id", groupId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []) as CalendarCandidate[];
}

export async function saveCalendarCandidate(input: {
  groupId: string;
  /** Required: calendar_candidates.season now has a FK to seasons(code). */
  season: string;
  label: string;
  source: "optimizer" | "circle" | "manual" | "draw";
  constraints: Record<string, unknown>;
  weights: Record<string, unknown>;
  pairings: NonNullable<CalendarGenerationResult["pairings"]>;
  metrics: Record<string, unknown>;
}): Promise<CalendarCandidate> {
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("calendar_candidates")
    .insert({
      league_group_id: input.groupId,
      season: input.season,
      label: input.label,
      source: input.source,
      constraints: input.constraints,
      weights: input.weights,
      pairings: input.pairings,
      metrics: input.metrics,
      created_by: userData.user?.id ?? null,
    })
    .select("*")
    .single();
  if (error) throw error;
  return data as CalendarCandidate;
}

export async function validateCalendarCandidate(
  candidateId: string
): Promise<{ committed?: boolean; validated?: boolean; total_matches?: number; error?: string }> {
  const { data, error } = await supabase.rpc("validate_calendar_candidate", {
    p_candidate_id: candidateId,
  });
  if (error) throw error;
  return data;
}

export async function deleteCalendarCandidate(id: string): Promise<void> {
  const { error } = await supabase.from("calendar_candidates").delete().eq("id", id);
  if (error) throw error;
}

/** Overwrite an existing candidate's pairings/metrics/label (manual in-place edit). */
export async function updateCalendarCandidate(
  id: string,
  patch: {
    label?: string;
    source?: "optimizer" | "circle" | "manual" | "draw";
    pairings?: NonNullable<CalendarGenerationResult["pairings"]>;
    metrics?: Record<string, unknown>;
    constraints?: Record<string, unknown>;
    weights?: Record<string, unknown>;
  }
): Promise<CalendarCandidate> {
  const { data: userData } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("calendar_candidates")
    .update({ ...patch, updated_by: userData.user?.id ?? null })
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data as CalendarCandidate;
}

type Pairing = NonNullable<CalendarGenerationResult["pairings"]>[number];

/** Derive pairings (+ legs/roundsPerLeg) from a flat set of committed matches. */
function buildPairingsFromMatches(
  matches: Array<{ game_day: number; match_date: string | null; home_team_id: string; away_team_id: string }>
): { pairings: Pairing[]; teamIds: string[]; legs: number; roundsPerLeg: number; n: number } {
  const teamIds = Array.from(new Set(matches.flatMap((m) => [m.home_team_id, m.away_team_id])));
  const seen = new Map<string, number>();
  for (const m of matches) {
    const key = [m.home_team_id, m.away_team_id].sort().join("|");
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  const legs = Array.from(seen.values()).some((v) => v >= 2) ? 2 : 1;
  const maxGd = Math.max(1, ...matches.map((m) => m.game_day || 1));
  const roundsPerLeg = Math.max(1, Math.round(maxGd / legs));
  const pairings: Pairing[] = matches.map((m) => ({
    leg: legs === 2 && m.game_day > roundsPerLeg ? 2 : 1,
    game_day: m.game_day,
    match_date: m.match_date ?? null,
    home_team_id: m.home_team_id,
    away_team_id: m.away_team_id,
  }));
  return { pairings, teamIds, legs, roundsPerLeg, n: teamIds.length };
}

/**
 * The committed (production) calendar for a group, rebuilt from `matches` as a
 * CalendarGenerationResult so the generator can show its analysis read-only.
 * `played` counts matches that have started/finished (lock trigger).
 */
export async function getCommittedCalendar(
  groupId: string
): Promise<{ result: CalendarGenerationResult; played: number } | null> {
  const { data, error } = await supabase
    .from("matches")
    .select("season,status,game_day,match_date,home_team_id,away_team_id,home_score,away_score")
    .eq("league_group_id", groupId)
    .order("game_day", { ascending: true });
  if (error) throw error;
  if (!data || data.length === 0) return null;
  const built = buildPairingsFromMatches(data as any);
  let played = 0;
  for (const m of data as any[]) {
    if (m.status === "in_progress" || m.status === "completed" || m.home_score != null || m.away_score != null) played++;
  }
  const result: CalendarGenerationResult = {
    group_id: groupId,
    season: (data[0] as any).season,
    teams: built.n,
    legs: built.legs,
    rounds_per_leg: built.roundsPerLeg,
    total_matches: data.length,
    pairings: built.pairings,
    committed: true,
  };
  return { result, played };
}

/**
 * Reset a group's calendar (delete its scheduled, unplayed matches) so a new one
 * can be committed. Returns { deleted, remaining }; remaining>0 means some matches
 * were kept (played, or have sent missions/sanctions).
 */
export async function resetCalendarForGroup(
  groupId: string
): Promise<{ deleted: number; remaining: number; candidates_reverted: number; error?: string }> {
  const { data, error } = await supabase.rpc("reset_calendar_for_group", {
    p_group_id: groupId,
  });
  if (error) throw error;
  const r = (data ?? {}) as {
    deleted?: number;
    remaining?: number;
    candidates_reverted?: number;
    error?: string;
  };
  return {
    deleted: r.deleted ?? 0,
    remaining: r.remaining ?? 0,
    candidates_reverted: r.candidates_reverted ?? 0,
    error: r.error,
  };
}

/**
 * Per-team strength (0–100) from a season's completed matches, for the calendar
 * optimizer's top-match spreading. Best team in the set scores 100.
 */
export async function getTeamStrengths(
  teamIds: string[],
  season: string
): Promise<Record<string, number>> {
  if (!teamIds.length) return {};
  const { data, error } = await supabase.rpc("get_team_strengths", {
    p_team_ids: teamIds,
    p_season: season,
  });
  if (error) throw error;
  const out: Record<string, number> = {};
  for (const row of (data ?? []) as Array<{ team_id: string; strength: number }>) {
    out[row.team_id] = row.strength ?? 0;
  }
  return out;
}

// ── Group teams (drag-and-drop board, "live" mode) ──────────────────────────

export async function setGroupTeams(groupId: string, teamIds: string[]): Promise<void> {
  // Replace the full team list of a group atomically.
  const { error: delErr } = await supabase
    .from("league_group_teams")
    .delete()
    .eq("league_group_id", groupId);
  if (delErr) throw delErr;
  if (teamIds.length === 0) return;
  const { error: insErr } = await supabase
    .from("league_group_teams")
    .insert(teamIds.map((team_id) => ({ league_group_id: groupId, team_id })));
  if (insErr) throw insErr;
}

// ── Cup configuration ───────────────────────────────────────────────────────

export interface CupQualifyingSource {
  group_id: string;
  top_n: number;
}

export interface CupConfig {
  qualifies_from: CupQualifyingSource[];
  promotes_winner_to_league_id: string | null;
}

export async function updateCupConfig(
  groupId: string,
  patch: Partial<CupConfig>
): Promise<void> {
  const { error } = await supabase
    .from("league_groups")
    .update(patch)
    .eq("id", groupId);
  if (error) throw error;
}

export async function inviteTopNToCup(
  cupGroupId: string,
  replace = false
): Promise<{ inserted_total: number; invited_team_ids: string[]; per_source: any[] }> {
  const { data, error } = await supabase.rpc("invite_top_n_to_cup", {
    p_cup_group_id: cupGroupId,
    p_replace: replace,
  });
  if (error) throw error;
  return data as any;
}

// ── Incoming teams (relegations from upper league, promotions from regional) ─

export interface SearchedTeam {
  id: string;
  name: string;
  code: string | null;
  name_ar: string | null;
  wilaya: string | null;
  fifa_id: string | null;
}

/**
 * Club rows (`club_team_id IS NULL`) of a league: every club placed in one of
 * its groups in any season, directly or through one of its category teams.
 */
export async function fetchLeagueClubIds(leagueId: string): Promise<string[]> {
  const rows: any[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from("league_group_teams")
      .select("team_id, team:teams(club_team_id), league_group:league_groups!inner(league_id)")
      .eq("league_group.league_id", leagueId)
      // Stable order: paging with .range() on an unordered read can skip/duplicate rows.
      .order("league_group_id", { ascending: true })
      .order("team_id", { ascending: true })
      .range(from, from + 999);
    if (error) throw error;
    rows.push(...(data ?? []));
    if (!data || data.length < 1000) break;
  }
  return clubIdsOfPlacements(rows.map((r) => ({ team_id: r.team_id, club_team_id: r.team?.club_team_id ?? null })));
}

/** `club_of(t) = coalesce(club_team_id, id)`, deduplicated. */
export function clubIdsOfPlacements(
  rows: readonly { team_id: string | null; club_team_id: string | null }[],
): string[] {
  const out = new Set<string>();
  for (const r of rows) {
    const club = r.club_team_id ?? r.team_id;
    if (club) out.add(club);
  }
  return Array.from(out);
}

export interface SearchTeamsScope {
  /**
   * Restrict to these club ids (see `fetchLeagueClubIds`). `null`/omitted =
   * every club row, only when the user explicitly widens the search.
   */
  clubIds?: readonly string[] | null;
}

const SEARCH_LIMIT = 20;
const CLUB_ID_CHUNK = 150;

export async function searchTeams(
  query: string,
  excludeIds: string[] = [],
  scope: SearchTeamsScope = {}
): Promise<SearchedTeam[]> {
  const build = () => {
    let q = supabase
      .from("teams")
      .select("id, name, code, name_ar, wilaya, fifa_id")
      .eq("is_archived", false)
      // Club rows only: category teams (U17…) are placed with their club.
      .is("club_team_id", null)
      .order("name")
      .limit(SEARCH_LIMIT);
    if (query.trim()) {
      const pat = buildTeamSearchPattern(query);
      // Search the FAF Latin name (name_faf) + abbreviation too: many clubs were
      // merged keeping an Arabic `name`, so their Latin name only lives in
      // name_faf — without this, "ES Sétif" / "USMS" wouldn't be findable.
      q = q.or(
        `name.ilike.${pat},name_ar.ilike.${pat},name_faf.ilike.${pat},abriv.ilike.${pat},code.ilike.${pat},fifa_id.ilike.${pat}`
      );
    }
    return q;
  };

  let rows: SearchedTeam[];
  if (scope.clubIds) {
    if (scope.clubIds.length === 0) return [];
    // Chunked so the id list never blows the URL; each chunk is already
    // name-ordered and capped, the merge keeps the first SEARCH_LIMIT names.
    const chunks: string[][] = [];
    for (let i = 0; i < scope.clubIds.length; i += CLUB_ID_CHUNK) {
      chunks.push(scope.clubIds.slice(i, i + CLUB_ID_CHUNK));
    }
    const results = await Promise.all(chunks.map((ids) => build().in("id", ids)));
    rows = [];
    for (const { data, error } of results) {
      if (error) throw error;
      rows.push(...((data ?? []) as SearchedTeam[]));
    }
    rows = mergeSearchResults(rows, SEARCH_LIMIT);
  } else {
    const { data, error } = await build();
    if (error) throw error;
    rows = (data ?? []) as SearchedTeam[];
  }
  return rows.filter((t) => !excludeIds.includes(t.id));
}

/** Merge name-ordered chunk results: dedupe, re-sort like `.order("name")`, cap. */
export function mergeSearchResults<T extends { id: string; name: string }>(rows: readonly T[], limit: number): T[] {
  const seen = new Set<string>();
  const unique = rows.filter((r) => (seen.has(r.id) ? false : (seen.add(r.id), true)));
  return unique
    .sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : 0))
    .slice(0, limit);
}

export async function createTeam(input: {
  name: string;
  code: string;
  name_ar?: string | null;
}): Promise<SearchedTeam> {
  const { data, error } = await supabase
    .from("teams")
    .insert({
      name: input.name,
      code: input.code,
      name_ar: input.name_ar ?? null,
    })
    .select("id, name, code, name_ar, wilaya, fifa_id")
    .single();
  if (error) throw error;
  return data as SearchedTeam;
}

export async function addIncomingTeamToGroup(args: {
  groupId: string;
  teamId: string;
  transitionId?: string | null;
  origin?: string | null;
  toLeagueId?: string | null;
}): Promise<void> {
  // 1) Place the team in the destination group. Trigger enforces the
  //    one-regular-group-per-season rule (cup groups exempt).
  const { error: lgtErr } = await supabase
    .from("league_group_teams")
    .insert({ league_group_id: args.groupId, team_id: args.teamId });
  if (lgtErr && lgtErr.code !== "23505") throw lgtErr;

  // 2) Optional audit row in team_moves when scoped to a transition
  if (args.transitionId) {
    const { data: userData } = await supabase.auth.getUser();
    await supabase
      .from("season_transition_team_moves")
      .upsert(
        {
          transition_id: args.transitionId,
          team_id: args.teamId,
          movement_type: "admitted",
          to_league_id: args.toLeagueId ?? null,
          to_group_id: args.groupId,
          notes: args.origin ?? null,
          decided_by: userData.user?.id ?? null,
          decided_at: new Date().toISOString(),
        },
        { onConflict: "transition_id,team_id" }
      );
  }
}

// ── Incoming teams awaiting placement by their destination league ───────────
// Cross-league moves (promotion/relegation into a league that is not operating
// the transition itself, e.g. LIRF → LRF/LNFA) are left without a group: the
// destination league places them when it onboards. RLS lets league users read
// the moves that target their league; placement goes through the
// place_incoming_team RPC (can_manage_league-gated).

export interface IncomingTeamMove {
  transition_id: string;
  team_id: string;
  from_league_id: string | null;
  to_league_id: string;
  to_group_id: string | null;
  movement_type: TeamMovement;
  notes: string | null;
  team: { id: string; name: string; name_ar: string | null; wilaya: string | null } | null;
  transition: { id: string; from_season: string; to_season: string; status: TransitionStatus };
}

export async function listIncomingTeamMoves(
  leagueId: string,
  toSeason: string
): Promise<IncomingTeamMove[]> {
  const { data, error } = await supabase
    .from("season_transition_team_moves")
    .select(
      "transition_id, team_id, from_league_id, to_league_id, to_group_id, movement_type, notes, " +
        "team:teams(id, name, name_ar, wilaya), " +
        "transition:season_transitions!inner(id, from_season, to_season, status)"
    )
    .eq("to_league_id", leagueId)
    .is("to_group_id", null)
    .in("movement_type", ["promotion", "relegation", "admitted"])
    .eq("transition.to_season", toSeason)
    .neq("transition.status", "rolled_back");
  if (error) throw error;
  return ((data ?? []) as unknown as IncomingTeamMove[]).filter(
    (m) => m.from_league_id !== m.to_league_id
  );
}

export async function placeIncomingTeam(args: {
  transitionId: string;
  teamId: string;
  groupId: string;
}): Promise<void> {
  const { error } = await supabase.rpc("place_incoming_team", {
    p_transition_id: args.transitionId,
    p_team_id: args.teamId,
    p_group_id: args.groupId,
  });
  if (error) throw error;
}

export async function moveTeamBetweenGroups(
  teamId: string,
  fromGroupId: string | null,
  toGroupId: string | null
): Promise<void> {
  if (fromGroupId) {
    const { error: delErr } = await supabase
      .from("league_group_teams")
      .delete()
      .eq("league_group_id", fromGroupId)
      .eq("team_id", teamId);
    if (delErr) throw delErr;
  }
  if (toGroupId) {
    const { error: insErr } = await supabase
      .from("league_group_teams")
      .insert({ league_group_id: toGroupId, team_id: teamId });
    if (insErr && insErr.code !== "23505") throw insErr; // ignore dup
  }
}
